import React, { useState, useRef, useEffect } from 'react';
import './DraggableDivider.css';

interface DraggableDividerProps {
  onResize: (delta: number) => void;
  orientation?: 'horizontal' | 'vertical';
}

const DraggableDivider: React.FC<DraggableDividerProps> = ({
  onResize,
  orientation = 'vertical'
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const startPosRef = useRef<number>(0);
  const dividerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      const delta = orientation === 'vertical' 
        ? e.clientX - startPosRef.current
        : e.clientY - startPosRef.current;
      
      onResize(delta);
      startPosRef.current = orientation === 'vertical' ? e.clientX : e.clientY;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = orientation === 'vertical' ? 'col-resize' : 'row-resize';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onResize, orientation]);

  const handleMouseDown = (e: React.MouseEvent) => {
    startPosRef.current = orientation === 'vertical' ? e.clientX : e.clientY;
    setIsDragging(true);
    e.preventDefault();
  };

  return (
    <div
      ref={dividerRef}
      className={`draggable-divider ${orientation} ${isDragging ? 'dragging' : ''}`}
      onMouseDown={handleMouseDown}
    >
      <div className="divider-handle" />
    </div>
  );
};

export default DraggableDivider;
